import { useLocalization } from '@lib/localization';
import SPInspection from '@lib/spproject/SPInspection';
import { Box, Typography } from '@mui/material';
import type { IInspection } from '@store/api/inspectionEndpoints';
import { useMemo } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { NoDataContainer, NoDataDescription, NoDataIcon, NoDataTitle } from './Overview.styled';
import useNoticeColors from '@store/hooks/useNoticeColors';
interface OverTimeChartProps {
  inspections: IInspection[]
}

interface ChartDataPoint {
  date: string
  ok: number
  warning: number
  error: number
  notChecked: number
}

const OvertimeChart = ({ inspections }: OverTimeChartProps) => {
  const { t, currentLanguage } = useLocalization();
  const noticeColors = useNoticeColors();

  const chartData = useMemo(() => {
    if (!inspections || inspections.length === 0) {
      return [];
    }
    const dateMap = new Map<string, ChartDataPoint>();

    inspections.forEach((inspection) => {
      const pois = inspection.pointOfInterests ?? [];

      const date = new Date(inspection.info.dateModified);
      const dateKey = date.toISOString().split('T')[0];

      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, {
          date: dateKey,
          ok: 0,
          warning: 0,
          error: 0,
          notChecked: 0,
        });
      }

      const dataPoint = dateMap.get(dateKey)!;

      let hasError = false;
      let hasWarning = false;
      let hasNotChecked = false;
      let allOk = true;

      pois.forEach((poi) => {
        const status = SPInspection.getPOIStatus(poi);
        if (status === 'Error') {
          hasError = true;
          allOk = false;
        } else if (status === 'Warning') {
          hasWarning = true;
          allOk = false;
        } else if (status === 'NotChecked') {
          hasNotChecked = true;
          allOk = false;
        }
      });

      if (hasError) {
        dataPoint.error++;
      } else if (hasWarning) {
        dataPoint.warning++;
      } else if (hasNotChecked) {
        dataPoint.notChecked++;
      } else if (allOk && pois.length > 0) {
        dataPoint.ok++;
      } else {
        dataPoint.notChecked++;
      }
    });

    const result = Array.from(dateMap.values());
    result.sort((a, b) => a.date.localeCompare(b.date));

    if (result.length == 1) result.unshift(result[0]);

    return result;
  }, [inspections]);

  const isEmpty = chartData.length === 0;

  const isOkLineExist = chartData.some(data => Boolean(data.ok));
  const isWarningLineExist = chartData.some(data => Boolean(data.warning));
  const isErrorLineExist = chartData.some(data => Boolean(data.error));
  const isNotCheckedLineExist = chartData.some(data => Boolean(data.notChecked));

  const getMaxValue = (data: ChartDataPoint[]) =>
    Math.max(...data.flatMap(item => [item.ok, item.warning, item.error, item.notChecked]));

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString(currentLanguage, { month: 'short', day: 'numeric' });

  const maxValue = getMaxValue(chartData);

  return (<>
    {isEmpty &&
      <NoDataContainer>
        <NoDataIcon />
        <NoDataTitle variant="h6">
          {t('Project_Overview_NoData')}
        </NoDataTitle>
        <NoDataDescription variant="body2">
          {t('Project_Overview_NoData_Subtitle')}
        </NoDataDescription>
      </NoDataContainer>
    }
    {!isEmpty &&
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', m: 2, mb: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: noticeColors.Ok, mr: 1 }} />
            <Typography variant="body2">{t('Common_Successful')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: noticeColors.Warning, mr: 1 }} />
            <Typography variant="body2">{t('Common_Warning')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: noticeColors.Error, mr: 1 }} />
            <Typography variant="body2">{t('Common_Error')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: noticeColors.NotChecked, mr: 1 }} />
            <Typography variant="body2">{t('Common_NotChecked')}</Typography>
          </Box>
        </Box>
        <Box sx={{ height: '400px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tickFormatter={formatDate}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                domain={[0, maxValue]}
                tickFormatter={(tick) => Math.floor(tick).toString()} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#030303',
                  border: '1px solid #333',
                  borderRadius: 8,
                  color: '#F1F1F2',
                  fontSize: 12,
                }}
                labelStyle={{
                  color: '#FFFFFF',
                  fontWeight: 'bold',
                }}
                itemStyle={{
                  color: '#E1E1E2',
                }}
              />
              {isOkLineExist && <Line
                type="monotone"
                dataKey="ok"
                name={t('Common_Successful')}
                stroke={noticeColors.Ok}
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 8 }}
                style={{ transform: 'translateY(-3px)' }}
              />}
              {isWarningLineExist && <Line
                type="monotone"
                dataKey="warning"
                name={t('Common_Warning')}
                stroke={noticeColors.Warning}
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 8 }}
                style={{ transform: 'translateY(-2px)' }}
              />}
              {isErrorLineExist && <Line
                type="monotone"
                dataKey="error"
                name={t('Common_Error')}
                stroke={noticeColors.Error}
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 8 }}
                style={{ transform: 'translateY(-1px)' }}
              />}
              {isNotCheckedLineExist && <Line
                type="monotone"
                dataKey="notChecked"
                name={t('Common_NotChecked')}
                stroke={noticeColors.NotChecked}
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 8 }}
              />}

              <defs>
                <linearGradient id="colorOk" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={noticeColors.Ok} stopOpacity={0.1} />
                  <stop offset="95%" stopColor={noticeColors.Ok} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorWarning" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={noticeColors.Warning} stopOpacity={0.1} />
                  <stop offset="95%" stopColor={noticeColors.Warning} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorError" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={noticeColors.Error} stopOpacity={0.1} />
                  <stop offset="95%" stopColor={noticeColors.Error} stopOpacity={0} />
                </linearGradient>
              </defs>
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    }
  </>
  );
};

export default OvertimeChart;
